import { useState, useEffect } from 'react';
import { Sparkles, Heart, MessageCircle, Wind, TrendingUp, Sun, Moon, Sunrise, Sunset, Pill, AlertTriangle, BookHeart, Flame, Phone, Mic, Settings2, Radio } from 'lucide-react';
import { AFFIRMATIONS, MOODS, MED_TIMES } from '../lib/constants';
import { getAffirmationOfDay, getMoodLog, addMoodLog, todayKey, getMedsLog, markMedTaken, getStreak, getSessionsForPatient, getPatient } from '../lib/mock';
import { scheduleMedicationReminders, scheduleAffirmations } from '../lib/notify';
import MedicationsManager from '../components/MedicationsManager';
import VoiceRecorder from '../components/VoiceRecorder';
import InstallPrompt from '../components/InstallPrompt';

const TIME_ICONS = { Sunrise, Sun, Sunset, Moon };

export default function HomeScreen({ patient: initialPatient, onNavigate }) {
  const [patient, setPatient] = useState(initialPatient);
  const [affirmation] = useState(getAffirmationOfDay(AFFIRMATIONS));
  const [todayMood, setTodayMood] = useState(null);
  const [streak, setStreak] = useState(0);
  const [medsLog, setMedsLog] = useState({});
  const [sessionsCount, setSessionsCount] = useState(0);
  const [showMedsMgr, setShowMedsMgr] = useState(false);
  const [showVoice, setShowVoice] = useState(false);
  const [showSession, setShowSession] = useState(false);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [sessionMinutes, setSessionMinutes] = useState(5);

  useEffect(() => { loadData(); scheduleMedicationReminders(patient.medications || []); }, []);

  const loadData = async () => {
    const log = await getMoodLog(patient.id);
    const t = log.find(m => m.date === todayKey());
    setTodayMood(t || null);
    setStreak(getStreak(log));
    setMedsLog(await getMedsLog(patient.id));
    setSessionsCount((await getSessionsForPatient(patient.id)).length);
    // Refresh mood-aware affirmations after we know the latest mood
    scheduleAffirmations(t?.code || (log[0]?.code));
  };

  const refreshPatient = async () => {
    try {
      const fresh = await getPatient(patient.id);
      if (fresh) { setPatient(fresh); scheduleMedicationReminders(fresh.medications || []); }
    } catch (e) { /* ignore */ }
  };

  const logMood = async (mood) => {
    const entry = { date: todayKey(), timestamp: new Date().toISOString(), code: mood.code, emoji: mood.emoji, score: mood.score, label: mood.label, note: '' };
    await addMoodLog(patient.id, entry); setTodayMood(entry); loadData();
  };
  const handleMedToggle = async (timeCode, medName, taken) => { await markMedTaken(patient.id, todayKey(), timeCode, medName, taken); setMedsLog(await getMedsLog(patient.id)); };

  // Build today's meds schedule
  const medsByTime = {};
  (patient.medications || []).forEach(m => {
    const med = typeof m === 'string' ? { name: m, schedule: ['morning', 'night'] } : m;
    (med.schedule || []).forEach(t => { if (!medsByTime[t]) medsByTime[t] = []; medsByTime[t].push(med.name); });
  });
  const takenToday = medsLog[todayKey()] || {};
  const totalDoses = Object.values(medsByTime).reduce((a, arr) => a + arr.length, 0);
  const takenCount = Object.values(takenToday).reduce((a, obj) => a + Object.values(obj || {}).filter(x => x?.taken).length, 0);

  return (
    <div className="px-4 pt-4 pb-2">
      {/* Affirmation Hero */}
      <div className="relative overflow-hidden rounded-3xl p-5 mb-4 animate-fade-in" style={{ background: 'linear-gradient(135deg, #ffd6a5, #ffb4a2 50%, #b8afff)' }}>
        <div className="absolute top-2 left-3 w-16 h-16 rounded-full bg-white/25 blur-xl"></div>
        <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-full bg-white/20 blur-2xl"></div>
        <div className="relative">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-lg flex items-center justify-center bg-white/30 backdrop-blur">
              <Sparkles className="w-3.5 h-3.5 text-white" fill="white" />
            </div>
            <span className="text-[11px] font-extrabold uppercase tracking-wider text-white/95">كلمات اليوم</span>
          </div>
          <p className="text-[15px] leading-[1.9] font-bold text-white mb-2 drop-shadow-sm">« {affirmation.text} »</p>
          <p className="text-[11px] text-left text-white/85 font-semibold">— {affirmation.author}</p>
        </div>
      </div>

      {/* Mood Check-in */}
      <div className="card-soft p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'var(--rose-bg)' }}>
              <Heart className="w-4 h-4" style={{ color: '#d17c53' }} fill="#ec9e9b" />
            </div>
            <div>
              <div className="text-[13px] font-extrabold" style={{ color: 'var(--text)' }}>كيف تشعرين الآن؟</div>
              <div className="text-[10.5px]" style={{ color: 'var(--text-2)' }}>سجّلي حالتك اليوم</div>
            </div>
          </div>
          {streak > 0 && (
            <div className="flex items-center gap-1 rounded-full px-2 py-1" style={{ background: 'var(--honey-bg)', border: '1px solid var(--honey)' }}>
              <Flame className="w-3 h-3" style={{ color: '#c9871f' }} fill="#f0b16c" />
              <span className="text-[10.5px] font-extrabold" style={{ color: '#8a5f1a' }}>{streak}</span>
            </div>
          )}
        </div>
        {todayMood ? (
          <div className="rounded-2xl px-4 py-3 flex items-center gap-3" style={{ background: 'var(--surface-2)' }}>
            <div className="text-3xl">{todayMood.emoji}</div>
            <div className="flex-1">
              <div className="text-[12.5px] font-extrabold" style={{ color: 'var(--text)' }}>{todayMood.label}</div>
              <div className="text-[10.5px]" style={{ color: 'var(--text-2)' }}>يمكنك تحديثه في أي وقت</div>
            </div>
            <button onClick={() => setTodayMood(null)} className="text-[11px] font-bold hover:underline" style={{ color: 'var(--primary-dark)' }}>تعديل</button>
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-1.5">
            {MOODS.map(m => (
              <button key={m.code} onClick={() => logMood(m)} className="flex flex-col items-center gap-1 py-2.5 rounded-2xl border transition-all hover:scale-105 active:scale-95" style={{ background: 'var(--surface-2)', borderColor: 'var(--border-soft)' }}>
                <div className="text-xl">{m.emoji}</div>
                <div className="text-[9.5px] font-bold" style={{ color: 'var(--text)' }}>{m.label}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Meds */}
      <div className="card-soft p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'var(--secondary-bg)' }}>
              <Pill className="w-4 h-4" style={{ color: 'var(--secondary-dark)' }} />
            </div>
            <div>
              <div className="text-[13px] font-extrabold" style={{ color: 'var(--text)' }}>جرعات اليوم</div>
              <div className="text-[10.5px]" style={{ color: 'var(--text-2)' }}>{totalDoses > 0 ? `${takenCount}/${totalDoses} مأخوذة` : 'لا توجد أدوية بعد'}</div>
            </div>
          </div>
          <button onClick={() => setShowMedsMgr(true)} className="flex items-center gap-1 rounded-full px-2.5 py-1.5 text-[10.5px] font-bold border" style={{ background: 'var(--surface-2)', borderColor: 'var(--border)', color: 'var(--text-2)' }} data-testid="home-manage-meds-btn">
            <Settings2 className="w-3 h-3" />
            إدارة
          </button>
        </div>
        {totalDoses > 0 ? (
          <>
            <div className="w-full h-1.5 rounded-full overflow-hidden mb-3" style={{ background: 'var(--border-soft)' }}>
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${totalDoses ? (takenCount / totalDoses * 100) : 0}%`, background: 'linear-gradient(90deg, #5eb2a3, #7dcfa9)' }}></div>
            </div>
            <div className="space-y-2">
              {MED_TIMES.filter(t => medsByTime[t.code]).map(t => {
                const Icon = TIME_ICONS[t.icon] || Sun;
                return (
                  <div key={t.code} className="rounded-2xl p-3" style={{ background: 'var(--surface-2)', border: '1px solid var(--border-soft)' }}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: `${t.color}22` }}>
                        <Icon className="w-3.5 h-3.5" style={{ color: t.color }} />
                      </div>
                      <span className="text-[11.5px] font-extrabold" style={{ color: 'var(--text)' }}>{t.label}</span>
                      <span className="text-[10px]" style={{ color: 'var(--text-2)' }}>{t.hour}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {medsByTime[t.code].map((med, i) => {
                        const taken = takenToday[t.code]?.[med]?.taken;
                        return (
                          <button key={i} onClick={() => handleMedToggle(t.code, med, !taken)} className={`flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-[11px] font-bold transition-all border ${taken ? 'shadow-sm' : 'active:scale-95'}`}
                            style={taken ? { background: 'var(--success-bg)', borderColor: 'var(--success)', color: '#3b8f66' } : { background: 'white', borderColor: 'var(--border)', color: 'var(--text)' }}>
                            <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${taken ? '' : ''}`}
                                 style={taken ? { background: 'var(--success)', borderColor: 'var(--success)' } : { borderColor: 'var(--text-3)' }}>
                              {taken && <div className="w-1.5 h-1.5 rounded-full bg-white"></div>}
                            </div>
                            {med}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <button onClick={() => setShowMedsMgr(true)} className="w-full py-3 rounded-2xl text-[12.5px] font-extrabold border-2 border-dashed transition-colors" style={{ borderColor: 'var(--secondary-light)', color: 'var(--secondary-dark)', background: 'var(--secondary-bg)' }} data-testid="home-add-first-med-btn">
            + أضيفي دواءك الأول
          </button>
        )}
      </div>

      {/* Voice + Mic Session */}
      <div className="grid grid-cols-2 gap-2.5 mb-4">
        <button onClick={() => setShowVoice(true)} className="relative overflow-hidden rounded-2xl p-3.5 text-right transition-all active:scale-95 hover:shadow-lg" style={{ background: 'linear-gradient(135deg, #b8afff, #8b7ff0)' }} data-testid="home-voice-btn">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center mb-2 bg-white/25 backdrop-blur">
            <Mic className="w-4.5 h-4.5 text-white" strokeWidth={2.4} />
          </div>
          <div className="text-[13px] font-extrabold text-white">رسالة صوتية</div>
          <div className="text-[10.5px] text-white/90">تحدثي، والذكاء يحلل</div>
        </button>
        <button onClick={() => setShowSession(true)} className="relative overflow-hidden rounded-2xl p-3.5 text-right transition-all active:scale-95 hover:shadow-lg" style={{ background: 'linear-gradient(135deg, #a4d6cc, #5eb2a3)' }} data-testid="home-session-btn">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center mb-2 bg-white/25 backdrop-blur">
            <Radio className="w-4.5 h-4.5 text-white" strokeWidth={2.4} />
          </div>
          <div className="text-[13px] font-extrabold text-white">جلسة استماع</div>
          <div className="text-[10.5px] text-white/90">للحظات الطويلة</div>
        </button>
      </div>


      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-2.5 mb-4">
        <QuickAction icon={MessageCircle} title="محادثة" subtitle="مع طبيبك" gradient="from-[#b8afff] to-[#8b7ff0]" onClick={() => onNavigate('chat')} />
        <QuickAction icon={Wind} title="تنفّس معي" subtitle="هدوء فوري" gradient="from-[#a4d6cc] to-[#5eb2a3]" onClick={() => onNavigate('tools')} />
        <QuickAction icon={BookHeart} title="يوميّاتي" subtitle="اكتبي شعورك" gradient="from-[#a3c9a1] to-[#7dcfa9]" onClick={() => onNavigate('tools')} />
        <QuickAction icon={TrendingUp} title="رحلتي" subtitle={`${sessionsCount} جلسة`} gradient="from-[#f5b892] to-[#ec9e9b]" onClick={() => onNavigate('journey')} />
      </div>

      {/* Emergency */}
      <div className="rounded-2xl p-3.5 flex items-center gap-3" style={{ background: 'var(--danger-bg)', border: '1px solid rgba(229,133,133,0.35)' }}>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(229,133,133,0.2)' }}>
          <Phone className="w-4.5 h-4.5" style={{ color: 'var(--danger)' }} />
        </div>
        <div className="flex-1">
          <div className="text-[12.5px] font-extrabold" style={{ color: '#a35555' }}>مساعدة عاجلة</div>
          <div className="text-[10.5px] leading-relaxed" style={{ color: '#b06b6b' }}>اتصلي بطبيبك أو 911 عند الحاجة</div>
        </div>
      </div>

      {/* Modals */}
      <MedicationsManager open={showMedsMgr} patient={patient} onClose={(updated) => { setShowMedsMgr(false); if (updated) { setPatient(updated); loadData(); } else { refreshPatient(); } }} />
      <VoiceRecorder open={showVoice} mode="chat" patient={patient} onClose={() => { setShowVoice(false); loadData(); }} />
      <SessionLengthPicker open={showSession && !sessionStarted} onPick={(min) => { setSessionMinutes(min); setSessionStarted(true); }} onClose={() => setShowSession(false)} />
      <VoiceRecorder open={showSession && sessionStarted} mode="session" sessionMinutes={sessionMinutes} patient={patient} onClose={() => { setShowSession(false); setSessionStarted(false); loadData(); }} />
      <InstallPrompt />
    </div>
  );
}

function SessionLengthPicker({ open, onPick, onClose }) {
  if (!open) return null;
  const options = [
    { m: 5, label: 'مناسبة لمحادثة سريعة' },
    { m: 10, label: 'نقاش هادئ أو خلاف' },
    { m: 30, label: 'جلسة عميقة' },
  ];
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in" data-testid="session-length-modal">
      <div className="w-full sm:max-w-sm bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl animate-slide-up">
        <div className="relative px-5 py-4" style={{ background: 'linear-gradient(135deg, #5eb2a3, #3f8579)' }}>
          <button onClick={onClose} className="absolute top-3 left-3 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center backdrop-blur" data-testid="session-len-close-btn">
            <span className="text-white text-lg leading-none">×</span>
          </button>
          <div className="text-center">
            <div className="text-white/90 text-[11px] font-bold uppercase tracking-wider mb-1">مدة الجلسة</div>
            <div className="text-white text-[16px] font-extrabold">اختاري مدة الاستماع</div>
          </div>
        </div>
        <div className="p-5 space-y-2.5">
          <p className="text-center text-[11.5px] leading-relaxed mb-2" style={{ color: 'var(--text-2)' }}>
            يستمع الذكاء إلى ما تقولين أو ما يدور حولك خلال المدة، ثم يحلل نبرتك وحالتك.
          </p>
          {options.map(({ m, label }) => (
            <button key={m} onClick={() => onPick(m)} className="w-full rounded-2xl px-4 py-3.5 flex items-center justify-between border-2 hover:shadow-md transition-all active:scale-[0.99]" style={{ borderColor: 'var(--border-soft)', background: 'var(--surface-2)' }} data-testid={`session-len-${m}`}>
              <span className="text-[14px] font-extrabold" style={{ color: 'var(--text)' }}>{m} دقائق</span>
              <span className="text-[11px]" style={{ color: 'var(--text-2)' }}>{label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function QuickAction({ icon: Icon, title, subtitle, gradient, onClick }) {
  return (
    <button onClick={onClick} className="relative overflow-hidden rounded-2xl p-3.5 text-right transition-all active:scale-95 hover:shadow-lg group" style={{ background: 'white', border: '1px solid var(--border-soft)' }}>
      <div className={`w-10 h-10 rounded-2xl flex items-center justify-center mb-2 bg-gradient-to-br ${gradient} shadow-md`}>
        <Icon className="w-4.5 h-4.5 text-white" strokeWidth={2.4} />
      </div>
      <div className="text-[13px] font-extrabold" style={{ color: 'var(--text)' }}>{title}</div>
      <div className="text-[10.5px]" style={{ color: 'var(--text-2)' }}>{subtitle}</div>
    </button>
  );
}
